import type {
  PlaybookId,
  PlaybookNode,
} from "@repo/app-types/entities/playbook";
import type { RecordingId } from "@repo/app-types/entities/recording";
import type { RecordingAssessmentFeedback } from "@repo/app-types/entities/recordingAssessmentFeedback";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { FieldError } from "@repo/ui/field/fieldError";
import { Size } from "@repo/ui/helpers/helpers";
import { Textarea } from "@repo/ui/Inputs/textarea";
import { ModalCloseButton } from "@repo/ui/Modal/modalCloseButton";
import { SimpleSelect } from "@repo/ui/Select/simpleSelect";
import { type FC, type FormEvent, useState } from "react";
import { Dialog, Header, Heading } from "react-aria-components";
import {
  usePostRecordingAssessmentFeedbackMutation,
  useUpdateRecordingAssessmentFeedbackMutation,
} from "../../api/entities/recording.js";
import { getApiErrorMessage } from "../../api/helpers/errors.js";

export interface GradingAssessmentFeedbackDialogProps {
  playbookId: PlaybookId;
  node: PlaybookNode;
  recordingId: RecordingId;
  grade: {
    value: string;
    commentary: string;
  };
  feedback: RecordingAssessmentFeedback | undefined;
}

export const GradingAssessmentFeedbackDialog: FC<
  GradingAssessmentFeedbackDialogProps
> = (props) => {
  const { node, feedback } = props;
  const [correctValue, setCorrectValue] = useState<string | null>(
    feedback?.data.correct_value ?? null,
  );
  const [explanation, setExplanation] = useState<string>(
    feedback?.data.explanation ?? "",
  );
  const [submitError, setSubmitError] = useState<string | null>(null);

  const isValid = correctValue !== null;

  const [postFeedback, { isLoading: isPosting }] =
    usePostRecordingAssessmentFeedbackMutation();

  const [updateFeedback, { isLoading: isUpdating }] =
    useUpdateRecordingAssessmentFeedbackMutation();

  const isLoading = isPosting || isUpdating;

  const handleSubmit = async (event: FormEvent, close: () => void) => {
    event.preventDefault();

    if (!isValid) {
      return;
    }
    setSubmitError(null);

    const feedbackNode =
      node.type === "section"
        ? {
            id: node.id,
            name: node.name,
            description: node.description,
            grading_value_type: "section" as const,
          }
        : node;

    const feedbackData = {
      criteria_node: feedbackNode,
      grading: props.grade,
      correct_value: correctValue,
      explanation,
    };

    try {
      if (feedback) {
        // Update existing feedback
        await updateFeedback({
          recordingId: props.recordingId,
          playbookId: props.playbookId,
          feedbackId: feedback.id,
          data: feedbackData,
        }).unwrap();
      } else {
        // Create new feedback
        await postFeedback({
          recordingId: props.recordingId,
          playbookId: props.playbookId,
          data: feedbackData,
        }).unwrap();
      }

      close();
    } catch (error) {
      setSubmitError(
        getApiErrorMessage(error, "Failed to save feedback. Please try again."),
      );
    }
  };

  const renderCorrectScoreField = () => {
    if (node.type === "section") {
      return (
        <SimpleSelect
          label="Expected result"
          placeholder="Does the section apply"
          options={[
            {
              label: "Section applies to this recording",
              value: "a",
            },
            {
              label: "Section does not apply",
              value: "n/a",
            },
          ]}
          value={correctValue}
          onChange={setCorrectValue}
        />
      );
    }

    switch (node.grading_value_type) {
      case "yes_no":
        return (
          <SimpleSelect
            label="What is the correct grade?"
            placeholder="Yes / No / Not applicable"
            options={[
              {
                label: "Yes",
                value: "yes",
              },
              {
                label: "No",
                value: "no",
              },
              {
                label: "Not applicable",
                value: "n/a",
              },
            ]}
            value={correctValue}
            onChange={setCorrectValue}
          />
        );

      case "1_5_score":
        return (
          <SimpleSelect
            label="What is the correct score?"
            placeholder="1 to 5 / Not applicable"
            options={[
              {
                label: "1 (Very poor)",
                value: "1",
              },
              {
                label: "2 (Poor)",
                value: "2",
              },
              {
                label: "3 (Acceptable)",
                value: "3",
              },
              {
                label: "4 (Good)",
                value: "4",
              },
              {
                label: "5 (Excellent)",
                value: "5",
              },
              {
                label: "Not applicable",
                value: "n/a",
              },
            ]}
            value={correctValue}
            onChange={setCorrectValue}
          />
        );
    }
  };

  return (
    <Dialog className="w-80 space-y-2 p-4">
      {({ close }) => (
        <>
          <Header className="flex items-center justify-between">
            <Heading slot="title" className="font-bold">
              AI grading feedback
            </Heading>

            <ModalCloseButton />
          </Header>

          <form
            className="space-y-3 [&_label]:text-xs [&_label]:font-normal [&_label]:text-muted-foreground"
            onSubmit={(event) => handleSubmit(event, close)}
          >
            <div>{renderCorrectScoreField()}</div>

            <div>
              <Textarea
                label="Explanation"
                placeholder="Provide a comment to help improve the AI grading"
                value={explanation}
                onChange={setExplanation}
                height="5rem"
              />
            </div>

            {submitError && (
              <div role="alert">
                <FieldError>{submitError}</FieldError>
              </div>
            )}

            <div className="flex justify-end">
              <Button
                type="submit"
                variant={ButtonVariant.Primary}
                size={Size.MEDIUM}
                isDisabled={!isValid || isLoading}
                isLoading={isLoading}
              >
                Save
              </Button>
            </div>
          </form>
        </>
      )}
    </Dialog>
  );
};
